// src/App.jsx

import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from 'react-router-dom';

import { prefetchPdfTemplates } from './pdf/clientPdf';
import { registerServiceWorker } from './services/notificationService';
import { avviaSincronizzazioneAutomatica } from './offline/outbox';

import CodaOffline from './components/CodaOffline';
import CookieNotice from './components/CookieNotice.jsx';
import Navbar from './components/Navbar.jsx';
import Footer from './components/Footer.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import LocationConsentGuard from './components/LocationConsentGuard.jsx';

// ==============================
// PAGINE PUBBLICHE
// ==============================

import Home from './pages/Home.jsx';
import Contatti from './pages/Contatti.jsx';
import Login from './pages/Login.jsx';
import Supporto from './pages/Supporto.jsx';
import Privacy from './pages/Privacy.jsx';
import Terms from './pages/Terms.jsx';

import RecuperoPassword from './pages/RecuperoPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';

// ==============================
// DASHBOARD / PROFILO
// ==============================

import Dashboard from './pages/Dashboard.jsx';
import Profilo from './pages/Profilo.jsx';
import Impostazioni from './pages/Impostazioni.jsx';
import ChangePassword from './pages/ChangePassword.jsx';

// ==============================
// UTENTI
// ==============================

import AggiungiUtente from './pages/AggiungiUtente.jsx';
import RimuoviUtente from './pages/RimuoviUtente.jsx';

// ==============================
// LAVORAZIONI
// ==============================

import Lavorazioni from './pages/Lavorazioni.jsx';
import LavorazioniCrea from './pages/LavorazioniCrea.jsx';
import LavorazioniModifica from './pages/LavorazioniModifica.jsx';
import LavorazioniAssegnate from './pages/LavorazioniAssegnate.jsx';
import LavorazioniInCorso from './pages/LavorazioniInCorso.jsx';
import LavorazioniTerminate from './pages/LavorazioniTerminate';
import LavorazioniAperte from './pages/LavorazioniAperte';
import LavorazioniGenerate from './pages/LavorazioniGenerate.jsx';

// ==============================
// REPORT
// ==============================

import ReportInterventi from './pages/ReportInterventi.jsx';
import ReportAssegnati from './pages/ReportAssegnati.jsx';

import VitAssegnati from './pages/VitAssegnati.jsx';
import ManutenzioniAssegnate from './pages/ManutenzioniAssegnate.jsx';

import VitCompilati from './pages/VitCompilati.jsx';
import ManutenzioniCompilate from './pages/ManutenzioniCopilate.jsx';

// ==============================
// MANUTENZIONE / VIT / GAS
// ==============================

import Manutenzione from './pages/Manutenzione.jsx';
import Vit from './pages/Vit.jsx';
import Gas from './pages/Gas.jsx';

import ManutenzioniMultiple from './pages/ManutenzioneMultiple.jsx';
import ManutenzioneMultipleCompila from './pages/ManutenzioneMultipleCompila';

// ==============================
// CALENDARIO
// ==============================

import Calendario from './pages/Calendario.jsx';
import Giustificativi from './pages/Giustificativi.jsx';
import CalendarioAziendale from './pages/CalendarioAziendale.jsx';

// ==============================
// TIMBRATURE
// ==============================

import Timbrature from './pages/Timbrature.jsx';
import TimbratureGestione from './pages/TimbratureGestione.jsx';

import ConsensoLocalizzazione from './pages/ConsensoLocalizzazione.jsx';
import CondizioniLocalizzazione from './pages/CondizioniLocalizzazione.jsx';

// ==============================
// FATTURAZIONE
// ==============================

import GestioneFatturazione from './pages/GestioneFatturazione.jsx';
import DashboardContabile from './pages/DashboardContabile.jsx';

// ==============================
// MAPPA
// ==============================

import MappaInterventi from './pages/MappaInterventi.jsx';

// ==============================
// CLIENTI
// ==============================

import DashboardCliente from './pages/DashboardCliente';
import ClienteAssocia from './pages/ClienteAssocia.jsx';
import MacchineCliente from './pages/MacchineCliente.jsx';

// ==============================
// PAGINE DA CLOTHO
// ==============================

import GestioneMacchine from './pages/GestioneMacchine.jsx';
import DispositiviSmart from './pages/DispositiviSmart.jsx';
import TelemetriaRealTime from './pages/TelemetriaRealTime.jsx';
import AllarmiSoglie from './pages/AllarmiSoglie.jsx';
import StoricoLetture from './pages/StoricoLetture.jsx';
import AggiornamentoFirmware from './pages/AggiornamentoFirmware.jsx';

function App() {
  React.useEffect(() => {
    /*
     * Il service worker mette in cache l'app.
     */
    registerServiceWorker().catch(() => {});

    /*
     * Se siamo già autenticati precarichiamo
     * i template PDF.
     */
    if (localStorage.getItem('token')) {
      prefetchPdfTemplates().catch(() => {});
    }

    /*
     * Spedisce automaticamente gli elementi
     * rimasti nella coda offline quando torna
     * la connessione.
     */
    return avviaSincronizzazioneAutomatica();
  }, []);

  return (
    <Router>
      <main className="flex-1">
        <div className="min-h-screen bg-gray-100">
          <CodaOffline />

          <Navbar />

          <Routes>
            {/* ============================== */}
            {/* PAGINE PUBBLICHE */}
            {/* ============================== */}

            <Route path="/" element={<Home />} />

            <Route
              path="/supporto"
              element={<Supporto />}
            />

            <Route
              path="/login"
              element={<Login />}
            />

            <Route
              path="/privacyPolicy"
              element={<Privacy />}
            />

            <Route
              path="/terms"
              element={<Terms />}
            />

            <Route
              path="/contatti"
              element={<Contatti />}
            />

            {/* ============================== */}
            {/* RECUPERO PASSWORD */}
            {/* ============================== */}

            <Route
              path="/recupero-password"
              element={<RecuperoPassword />}
            />

            <Route
              path="/reset-password/:token"
              element={<ResetPassword />}
            />

            {/* ============================== */}
            {/* CONSENSO LOCALIZZAZIONE */}
            {/* FUORI DAL GUARD */}
            {/* ============================== */}

            <Route
              path="/localizzazione/consenso"
              element={<ConsensoLocalizzazione />}
            />

            {/*
              Alias vecchio.

              Lo lasciamo intenzionalmente
              perché qualche componente potrebbe
              ancora navigare qui.
            */}
            <Route
              path="/consenso-localizzazione"
              element={<ConsensoLocalizzazione />}
            />

            <Route
              path="/condizioni-consenso-localizzazione"
              element={<CondizioniLocalizzazione />}
            />

            {/* ============================== */}
            {/* ROUTE SOTTO CONSENSO */}
            {/* ============================== */}

            <Route element={<LocationConsentGuard />}>
              <Route
                path="/dashboard"
                element={<Dashboard />}
              />

              <Route
                path="/timbrature"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      'admin',
                      'segreteria',
                      'gestione',
                      'tecnico',
                    ]}
                  >
                    <Timbrature />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/timbrature/gestione"
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      'admin',
                      'segreteria',
                      'gestione',
                    ]}
                  >
                    <TimbratureGestione />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* ============================== */}
            {/* UTENTI */}
            {/* ============================== */}

            <Route
              path="/aggiungi-utente"
              element={<AggiungiUtente />}
            />

            <Route
              path="/rimuovi-utente"
              element={<RimuoviUtente />}
            />

            {/* ============================== */}
            {/* PROFILO / IMPOSTAZIONI */}
            {/* ============================== */}

            <Route
              path="/profilo"
              element={<Profilo />}
            />

            <Route
              path="/impostazioni"
              element={<Impostazioni />}
            />

            <Route
              path="/cambia-password"
              element={<ChangePassword />}
            />

            {/* ============================== */}
            {/* MANUTENZIONE / VIT */}
            {/* ============================== */}

            <Route
              path="/manutenzione"
              element={<Manutenzione />}
            />

            <Route
              path="/vit"
              element={<Vit />}
            />

            <Route
              path="/gas"
              element={<Gas />}
            />

            {/* ============================== */}
            {/* CALENDARIO */}
            {/* ============================== */}

            <Route
              path="/calendario"
              element={<Calendario />}
            />

            <Route
              path="/calendario/giustificativi"
              element={<Giustificativi />}
            />

            <Route
              path="/calendario/azienda"
              element={<CalendarioAziendale />}
            />

            {/* ============================== */}
            {/* LAVORAZIONI */}
            {/* ============================== */}

            <Route
              path="/lavorazioni"
              element={<Lavorazioni />}
            />

            <Route
              path="/lavorazioni/crea"
              element={<LavorazioniCrea />}
            />

            <Route
              path="/lavorazioni/modifica"
              element={<LavorazioniModifica />}
            />

            <Route
              path="/lavorazioni/assegnate"
              element={<LavorazioniAssegnate />}
            />

            <Route
              path="/lavorazioni/in-corso"
              element={<LavorazioniInCorso />}
            />

            <Route
              path="/lavorazioni/terminate"
              element={<LavorazioniTerminate />}
            />

            <Route
              path="/lavorazioni/aperte"
              element={<LavorazioniAperte />}
            />

            <Route
              path="/lavorazioni/generate"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'admin',
                    'segreteria',
                    'gestione',
                  ]}
                >
                  <LavorazioniGenerate />
                </ProtectedRoute>
              }
            />

            {/* ============================== */}
            {/* REPORT */}
            {/* ============================== */}

            <Route
              path="/report-interventi"
              element={<ReportInterventi />}
            />

            <Route
              path="/report-assegnati"
              element={<ReportAssegnati />}
            />

            <Route
              path="/report-assegnati/vit"
              element={<VitAssegnati />}
            />

            <Route
              path="/report-assegnati/manutenzioni"
              element={<ManutenzioniAssegnate />}
            />

            <Route
              path="/lavorazioni-in-corso/vit"
              element={<VitCompilati />}
            />

            <Route
              path="/lavorazioni-in-corso/manutenzioni"
              element={<ManutenzioniCompilate />}
            />

            {/* ============================== */}
            {/* FATTURAZIONE */}
            {/* ============================== */}

            <Route
              path="/gestione-fatturazione"
              element={<GestioneFatturazione />}
            />

            <Route
              path="/dashboard-contabile"
              element={<DashboardContabile />}
            />

            {/* ============================== */}
            {/* MAPPA */}
            {/* ============================== */}

            <Route
              path="/mappa-interventi"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'admin',
                    'gestione',
                  ]}
                >
                  <MappaInterventi />
                </ProtectedRoute>
              }
            />

            {/* ============================== */}
            {/* MANUTENZIONI MULTIPLE */}
            {/* ============================== */}

            <Route
              path="/manutenzioni-multiple"
              element={<ManutenzioniMultiple />}
            />

            <Route
              path="/manutenzioni-multiple/compila"
              element={
                <ManutenzioneMultipleCompila />
              }
            />

            {/* ============================== */}
            {/* CLIENTI */}
            {/* ============================== */}

            <Route
              path="/dashboard-cliente"
              element={
                <ProtectedRoute
                  allowedRoles={['cliente']}
                >
                  <DashboardCliente />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cliente-associa"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'admin',
                    'segreteria',
                  ]}
                >
                  <ClienteAssocia />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cliente-macchine"
              element={
                <ProtectedRoute
                  allowedRoles={['cliente']}
                >
                  <MacchineCliente />
                </ProtectedRoute>
              }
            />

            {/* ============================== */}
            {/* CLOTHO */}
            {/* ============================== */}

            <Route
              path="/gestione-macchine"
              element={<GestioneMacchine />}
            />

            <Route
              path="/dispositivi-smart"
              element={<DispositiviSmart />}
            />

            <Route
              path="/aggiornamento-firmware"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'admin',
                    'segreteria',
                    'gestione',
                    'tecnico',
                  ]}
                >
                  <AggiornamentoFirmware />
                </ProtectedRoute>
              }
            />

            <Route
              path="/telemetria-real-time"
              element={<TelemetriaRealTime />}
            />

            <Route
              path="/allarmi-soglie"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'admin',
                    'segreteria',
                    'gestione',
                  ]}
                >
                  <AllarmiSoglie />
                </ProtectedRoute>
              }
            />

            <Route
              path="/storico-letture"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'admin',
                    'segreteria',
                    'gestione',
                  ]}
                >
                  <StoricoLetture />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </main>

      <Footer />

      <CookieNotice />
    </Router>
  );
}

export default App;